import { useMemo } from 'react'
import cn from 'classnames'
import { ShoppingCartOutlined } from '@ant-design/icons'
import {
  getImagesFromVariantOrPlaceholder,
  IProductSearch,
} from '@core/api/Products/types'
import { LYSButton, LYSCard, LYSTypography } from '@core/components/Primitives'
import ProductDetailLink from '@core/components/ProductCollection/ProductDetailLink'
import ProductPrice from '@core/components/shared/ProductPrice'
import useCart from '@core/hooks/cart/useCart'
import { useServices } from '@core/utils/ioc'
import { ProductContextProvider } from '@core/utils/models/product/ProductContext'
import style from './index.module.less'

interface Props {
  product: IProductSearch
  isSelected: boolean
}

const ProductVariationSwitchItem: React.FC<Props> = ({
  product,
  isSelected,
}) => {
  const { productContextService } = useServices()
  const variantImages = getImagesFromVariantOrPlaceholder(product.mainVariant)
  const { addToCart, isCartUpdatePending } = useCart()
  const productContext = useMemo(
    () =>
      productContextService.getProductSearchContext(
        product,
        product.mainVariant
      ),
    [productContextService, product]
  )

  const handleAddToCart = () => {
    addToCart(product.mainVariant, product.name, 1)
  }

  return (
    <ProductContextProvider productContext={productContext}>
      <LYSCard
        withBoxShadow={true}
        size={'small'}
        className={
          isSelected
            ? cn(style.variationSwitch, style.selected)
            : style.variationSwitch
        }
      >
        <ProductDetailLink
          product={product}
          slug={product.mainVariant.slug}
          id={product.mainVariant.id}
        >
          <img
            className={style.image}
            src={variantImages[0].url}
            alt={product.name}
            title={product.name}
            loading="lazy"
            width={200}
            height={200}
          />
          <div className={style.productInfo}>
            <div className={style.title}>
              <LYSTypography.Text>{product.name}</LYSTypography.Text>
              <br />

              <LYSTypography.Text type={'secondary'}>
                {product.variationName}
              </LYSTypography.Text>
            </div>

            <div className={style.price}>
              <LYSTypography.Text>
                <ProductPrice
                  price={product.mainVariant.packagingUnits[0].price}
                  compactPrice={true}
                  unitPrice={product.mainVariant.packagingUnits[0].unitPrice}
                  strikePrice={
                    product.mainVariant.packagingUnits[0].strikeThroughPrice
                      ?.price
                  }
                />
              </LYSTypography.Text>
            </div>
          </div>
        </ProductDetailLink>

        <LYSButton
          type={'primary'}
          ghost={true}
          icon={<ShoppingCartOutlined rev={undefined} />}
          className={style.addToCart}
          onClick={handleAddToCart}
          disabled={!productContext.rules.isPurchasable || isCartUpdatePending}
        />
      </LYSCard>
    </ProductContextProvider>
  )
}

export default ProductVariationSwitchItem
